import {
  ACCEPTED_DOCUMENT_EXTENSIONS,
  ACCEPTED_DOCUMENT_MIME_TYPES,
} from '@shared/schemas/document';

export type FolderTraversalResult = {
  acceptedFiles: File[];
  ignoredCount: number;
};

const ACCEPTED_MIME_SET = new Set<string>(ACCEPTED_DOCUMENT_MIME_TYPES);
const ACCEPTED_EXT_SET = new Set<string>(ACCEPTED_DOCUMENT_EXTENSIONS);

export function isAcceptedFile(file: File): boolean {
  if (file.type && ACCEPTED_MIME_SET.has(file.type)) {
    return true;
  }

  const lowerName = file.name.toLowerCase();
  const dotIndex = lowerName.lastIndexOf('.');
  if (dotIndex < 0) {
    return false;
  }

  return ACCEPTED_EXT_SET.has(lowerName.slice(dotIndex));
}

export function partitionFiles(files: Iterable<File>): FolderTraversalResult {
  const acceptedFiles: File[] = [];
  let ignoredCount = 0;

  for (const file of files) {
    if (isAcceptedFile(file)) {
      acceptedFiles.push(file);
    } else {
      ignoredCount += 1;
    }
  }

  return { acceptedFiles, ignoredCount };
}

type FsEntry = {
  isFile: boolean;
  isDirectory: boolean;
  file?: (success: (file: File) => void, error?: (e: unknown) => void) => void;
  createReader?: () => {
    readEntries: (success: (entries: FsEntry[]) => void, error?: (e: unknown) => void) => void;
  };
};

async function readEntryAsFile(entry: FsEntry): Promise<File | null> {
  if (!entry.isFile || !entry.file) {
    return null;
  }

  return new Promise<File | null>((resolve) => {
    entry.file!(
      (file) => resolve(file),
      () => resolve(null),
    );
  });
}

async function readDirectoryEntries(entry: FsEntry): Promise<FsEntry[]> {
  if (!entry.isDirectory || !entry.createReader) {
    return [];
  }

  const reader = entry.createReader();
  const allEntries: FsEntry[] = [];

  while (true) {
    const batch = await new Promise<FsEntry[]>((resolve) => {
      reader.readEntries(
        (entries) => resolve(entries),
        () => resolve([]),
      );
    });
    if (batch.length === 0) {
      break;
    }
    allEntries.push(...batch);
  }

  return allEntries;
}

async function walkEntry(entry: FsEntry, out: File[]): Promise<void> {
  if (entry.isFile) {
    const file = await readEntryAsFile(entry);
    if (file) {
      out.push(file);
    }
    return;
  }

  if (entry.isDirectory) {
    const children = await readDirectoryEntries(entry);
    for (const child of children) {
      await walkEntry(child, out);
    }
  }
}

export async function collectFilesFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
  const items = dataTransfer.items;
  if (!items || items.length === 0) {
    return Array.from(dataTransfer.files ?? []);
  }

  const entries: FsEntry[] = [];
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    if (!item) {
      continue;
    }

    const getter = (item as unknown as { webkitGetAsEntry?: () => FsEntry | null }).webkitGetAsEntry;
    const entry = typeof getter === 'function' ? getter.call(item) : null;
    if (entry) {
      entries.push(entry);
    }
  }

  if (entries.length === 0) {
    return Array.from(dataTransfer.files ?? []);
  }

  const collected: File[] = [];
  for (const entry of entries) {
    await walkEntry(entry, collected);
  }

  return collected;
}
